import type { CSSProperties } from 'react';
import { Icon, V2Card, V2CardHeader, V2Tabs } from '@sakhtar/design-system/react';
import { faDigits } from '../../lib/faDigits.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { FiscalPeriod } from '../../lib/types/finance.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html, frame `/t/kimia/finance`,
 * «سال مالی ۱۴۰۴» card — a tile per monthly period (number, name, meta,
 * state icon) plus the closing note. Tiles are real periods from
 * `GET .../accounting/fiscal-periods`; selecting one drives the checklist card.
 */

const TILE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  padding: '10px 11px',
  borderRadius: 12,
  textAlign: 'start',
  cursor: 'pointer',
  font: 'inherit',
};

function tileStyle(closed: boolean, current: boolean, selected: boolean): CSSProperties {
  if (closed) {
    return {
      ...TILE,
      background: 'var(--sk-bg-sunken)',
      border: '1px solid var(--sk-border)',
      color: 'var(--sk-fg-muted)',
      boxShadow: selected ? '0 0 0 2px var(--sk-mod-c)' : 'none',
    };
  }
  return {
    ...TILE,
    background: current
      ? 'color-mix(in oklch, var(--sk-mod-c) 16%, var(--sk-bg-elev))'
      : 'color-mix(in oklch, var(--sk-mod-c) 6%, var(--sk-bg-elev))',
    border: current
      ? '1px solid var(--sk-mod-c)'
      : '1px dashed color-mix(in oklch, var(--sk-mod-c) 45%, transparent)',
    color: 'var(--sk-fg)',
    boxShadow: selected ? '0 0 0 2px var(--sk-mod-c)' : 'none',
  };
}

export function PeriodYearCard({
  years,
  yearId,
  onYearChange,
  periods,
  selectedPeriodId,
  onSelectPeriod,
  isCurrent,
}: {
  /** Fiscal years from `GET .../finance/fiscal-years` (id + name only). */
  years: readonly { readonly fiscalYearId: string; readonly name: string }[];
  yearId: string | null;
  onYearChange: (fiscalYearId: string) => void;
  /** Periods of the selected year, in chronological order. */
  periods: readonly FiscalPeriod[];
  selectedPeriodId: string | null;
  onSelectPeriod: (fiscalPeriodId: string) => void;
  isCurrent: (p: FiscalPeriod) => boolean;
}) {
  const year = years.find((y) => y.fiscalYearId === yearId);
  const first = periods[0];
  const last = periods[periods.length - 1];
  return (
    <V2Card>
      <V2CardHeader
        icon="calendar"
        title={year?.name ?? 'سال مالی'}
        subtitle={
          first && last
            ? `${faDigits(periods.length)} دورهٔ ماهانه · ${first.name} تا ${last.name}`
            : 'دوره‌ای برای این سال ثبت نشده'
        }
        actions={
          years.length > 1 && yearId ? (
            <V2Tabs
              items={years.map((y) => ({ id: y.fiscalYearId, label: y.name }))}
              activeId={yearId}
              onChange={onYearChange}
            />
          ) : undefined
        }
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))',
          gap: 8,
          padding: 14,
        }}
      >
        {periods.map((p, i) => {
          const closed = p.postingState === 'closed';
          const current = !closed && isCurrent(p);
          const selected = p.fiscalPeriodId === selectedPeriodId;
          return (
            <button
              key={p.fiscalPeriodId}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectPeriod(p.fiscalPeriodId)}
              style={tileStyle(closed, current, selected)}
              title={`${displayJalaliDate(p.startDate)} — ${displayJalaliDate(p.endDate)}`}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <span className="sk-num" style={{ fontSize: 11, opacity: 0.7 }}>
                  {faDigits(i + 1)}
                </span>
                <span style={{ display: 'flex', opacity: closed ? 0.8 : 1 }}>
                  <Icon name={closed ? 'lock' : 'calendar'} size="sm" />
                </span>
              </span>
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{p.name}</span>
              <span className="sk-num" style={{ fontSize: 11.5, opacity: 0.82 }}>
                {closed ? 'بسته' : current ? 'باز · جاری' : 'باز'}
              </span>
            </button>
          );
        })}
      </div>
      <div
        style={{
          fontSize: 12.5,
          lineHeight: 1.8,
          color: 'var(--sk-fg-muted)',
          padding: '10px 14px',
          borderTop: '1px dashed var(--sk-divider)',
        }}
      >
        با بستن دوره، ثبت سند با تاریخ آن دوره غیرفعال می‌شود؛ بازگشایی فقط با دسترسی مدیر مالی
        انجام می‌شود.
      </div>
    </V2Card>
  );
}
